import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { Timestamp, addDoc, collection } from "firebase/firestore";
import { db } from "../firebase/config";
import { LEAD_TYPES, submitLeadEmail } from "../services/leadEmail";
import { DEALERSHIP_OPTIONS, getDealershipByEmail } from "../data/dealerships";
import { getInTouchSchema } from "../lib/leadFormSchemas";
import { Button } from "./ui/button";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "./ui/field";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Textarea } from "./ui/textarea";

export const GetInTouch = ({
  model,
  copy,
  conversionSendTo,
  collectionName: collectionNameOverride,
}) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(getInTouchSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      dealership: "",
      text: "",
    },
  });
  const navigate = useNavigate();
  const collectionName = collectionNameOverride || model + "GetInTouch";

  const sendGetinTouch = async (data) => {
    const selectedDealership = getDealershipByEmail(data.dealership);

    await addDoc(collection(db, collectionName), {
      name: data.name,
      email: data.email,
      phone: data.phone,
      dealership: data.dealership,
      text: data.text,
      date: Timestamp.fromDate(new Date()),
      model: model,
    });

    if (conversionSendTo && window.gtag) {
      window.gtag("event", "conversion", {
        send_to: conversionSendTo,
      });
    }

    await submitLeadEmail({
      type: LEAD_TYPES.GET_IN_TOUCH,
      customer: {
        name: data.name,
        email: data.email,
        phone: data.phone,
      },
      dealership: {
        email: data.dealership,
        label: selectedDealership?.label ?? "",
      },
      vehicle: {
        model,
      },
      campaign: {
        slug: `${model}-get-in-touch`,
      },
      source: {
        pageUrl: window.location.href,
      },
      message: data.text,
    });

    navigate("/thank-you-offer");
  };

  return (
    <div>
      <form
        onSubmit={handleSubmit(sendGetinTouch)}
        className="mx-auto w-full max-w-2xl px-6 py-10 sm:px-8"
        noValidate
      >
        <h2 className="text-xl text-center uppercase text-fordBlue">
          Свържи се с търговец
        </h2>
        <FieldGroup className="mt-8 gap-5">
          <Field data-invalid={!!errors.name}>
            <FieldLabel htmlFor="name">Име *</FieldLabel>
            <Input id="name" aria-invalid={!!errors.name} {...register("name")} />
            <FieldError>{errors.name?.message}</FieldError>
          </Field>

          <Field data-invalid={!!errors.email}>
            <FieldLabel htmlFor="email">Имейл *</FieldLabel>
            <Input
              id="email"
              type="email"
              placeholder="email@email.com"
              aria-invalid={!!errors.email}
              {...register("email")}
            />
            <FieldError>{errors.email?.message}</FieldError>
          </Field>

          <Field data-invalid={!!errors.phone}>
            <FieldLabel htmlFor="phone">Телефон *</FieldLabel>
            <Input
              id="phone"
              type="text"
              placeholder="+359 87 654321"
              aria-invalid={!!errors.phone}
              {...register("phone")}
            />
            <FieldError>{errors.phone?.message}</FieldError>
          </Field>

          <Field data-invalid={!!errors.dealership}>
            <FieldLabel>Предпочитано представителство *</FieldLabel>
            <Controller
              name="dealership"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                >
                  <SelectTrigger
                    className="w-full min-h-11"
                    aria-invalid={!!errors.dealership}
                  >
                    <SelectValue placeholder="Моля, изберете" />
                  </SelectTrigger>
                  <SelectContent>
                    {DEALERSHIP_OPTIONS.map((dealership) => (
                      <SelectItem
                        key={dealership.email}
                        value={dealership.email}
                      >
                        {dealership.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            <FieldError>{errors.dealership?.message}</FieldError>
          </Field>

          <Field data-invalid={!!errors.text}>
            <FieldLabel htmlFor="text">Запитване *</FieldLabel>
            <Textarea
              id="text"
              rows={8}
              aria-invalid={!!errors.text}
              {...register("text")}
            />
            <FieldError>{errors.text?.message}</FieldError>
          </Field>
        </FieldGroup>

        <Button
          type="submit"
          size="lg"
          className="mt-8 min-h-11 px-8 bg-fordBlue text-white hover:bg-fordLight"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Изпращане..." : "Изпрати"}
        </Button>
      </form>
    </div>
  );
};
